import React, { useState, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { v4 as uuidv4 } from "uuid";
import { debounce } from "lodash";

import { useAtom } from "jotai";
import { isAdminAtom, themeAtom } from "../../../../-Atoms";
import { db } from "../../../../firebaseConfig";
import { collection, onSnapshot, doc, updateDoc, setDoc, query, where, getDocs } from "firebase/firestore";

const ClientSelector = ({ jobData }) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [clientData, setClientData] = useState([]);

  const [client, setClient] = useState(jobData.client);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [, setOpen] = useState(false);

  useEffect(() => {
    setClient(jobData.client);
  }, [jobData]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "ClientDatabase"),
        (snapshot) => {
          const clients = snapshot.docs.map((doc) => ({
            id: doc.id,
            brand: doc.data().brand,
            noRetouching: doc.data().noRetouching,
          }));
          setClientData(clients);
        },
        (error) => {
          console.error("Error fetching clients:", error);
        }
      );

    return () => unsubscribe();
  }, []);

  const handleClientChange = (event, newValue) => {
    if (!isAdmin) return;

    if (newValue === null) {
      setClient("");

      if (jobData && jobData.id) {
        updateDoc(doc(db, "JobData", jobData.id),
        {
          client: "",
          clientArray: [],
          clientLowerCaseArray: [],
        },
        { merge: true }
      )
          .catch((error) => {
            console.error("Error clearing client:", error);
            setClient(jobData.client);
          });
      } else {
        console.error("Cannot update client: jobData.id is missing");
      }
      return;
    }

    let value = newValue;
    setClient(value);

    if (value === "Add New Client...") {
      console.log("new client");
    }

    let selectedClientData;
    for (let i = 0; i < clientData.length; i++) {
      if (clientData[i].brand === value) {
        selectedClientData = clientData[i];
        break;
      }
    }

    let daysToAdd = 5;
    if (selectedClientData) {
      if (selectedClientData.turnAround) {
        daysToAdd = selectedClientData.turnAround;
      }
    }

    const dd = addBusinessDays(
      new Date(jobData.bookedStart || new Date()),
      daysToAdd
    );

    let noRetouching = false;

    if (selectedClientData?.noRetouching) {
      noRetouching = selectedClientData.noRetouching;
    }

    if (value && jobData && jobData.id) {
      addChecklists(jobData.id, value, jobData);
    }

    if (jobData && jobData.jobNumber && jobData.jobType) {
      updateJobName(
        jobData.jobNumber,
        value,
        jobData.multipleDays,
        jobData.jobType
      );
    } else {
      console.error(
        "Cannot update job name: missing required job data properties"
      );
    }

    const clientArray = [];
    const clientLowerCaseArray = [];
    for (let i = 1; i < value.length + 1; i++) {
      clientArray.push(value.substring(0, i));
    }
    for (let i = 1; i < value.toLowerCase().length + 1; i++) {
      clientLowerCaseArray.push(value.toLowerCase().substring(0, i));
    }

    if (jobData && jobData.id) {
      updateDoc(doc(db, "JobData", jobData.id),
        {
          client: value,
          clientArray: clientArray,
          clientLowerCaseArray: clientLowerCaseArray,
          dueDate: dd.toISOString(),
          noRetouching: noRetouching,
        },
        { merge: true }
      )
        .catch((error) => {
          console.error("Error updating client:", error);
          setClient(jobData.client);
        });
    } else {
      console.error("Cannot update client: jobData.id is missing");
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    }),
    hover: { scale: 1.03 },
    tap: { scale: 0.98 },
  };

  const updateJobName = debounce(
    (jobNumberChange, clientChange) => {
      try {
        const jn = `${jobNumberChange}-${clientChange}`;

        setDoc(doc(db, "JobData", jobData.id), { jobName: jn || null }, { merge: true });
      } catch (e) {}
    },
  );

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 2,
        position: "relative",
        "& .MuiInputLabel-root": {
          color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          fontWeight: 500,
          fontSize: "0.95rem",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, 7px) scale(0.75)",
          },
        },
        "& .MuiInputBase-root": {
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          "&:hover": {
            backgroundColor:
              theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
            boxShadow:
              theme === "dark"
                ? `0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)`
                : `0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)`,
          },
          "&.Mui-focused": {
            boxShadow:
              theme === "dark"
                ? `0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)`
                : `0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)`,
          },
        },
      }}
      variant="filled"
    >
      <Autocomplete
        disablePortal
        options={clientData
          .map((c) => c.brand)
          .filter(Boolean)
          .sort()}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            InputProps={{ ...params.InputProps, disableUnderline: true }}
            error={client ? false : true}
            label="Client"
          />
        )}
        value={client || ""}
        onChange={handleClientChange}
        renderOption={(props, option, { index }) => {
          const isSelected = client === option;
          const isHovered = hoverIndex === index;

          return (
            <li
              {...props}
              key={option}
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
              style={{
                padding: "10px 16px",
                margin: "2px 6px",
                borderRadius: "8px",
                backgroundColor: isSelected
                  ? theme === "dark"
                    ? "rgba(100,100,255,0.2)"
                    : "rgba(100,100,255,0.15)"
                  : "transparent",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                fontWeight: isSelected ? 600 : isHovered ? 500 : 400,
                letterSpacing: isSelected ? "0.01em" : "normal",
                color: isSelected
                  ? theme === "dark"
                    ? "#ffffff"
                    : "#000000"
                  : theme === "dark"
                  ? "rgba(255,255,255,0.85)"
                  : "rgba(0,0,0,0.85)",
              }}
            >
              {option}
            </li>
          );
        }}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setHoverIndex(null);
        }}
      />
    </FormControl>
  );
};

export default ClientSelector;

async function addChecklists(id, client, jobData) {
  try {
    if (!id || !client || !jobData) {
      console.error("Missing required parameters for addChecklists");
      return;
    }

    const clientDataRaw = await getDocs(query(collection(db, "ClientDatabase"), where("brand", "==", client)));

    if (clientDataRaw.empty) {
      console.log(`No client found with brand name: ${client}`);
      return;
    }

    const clientData = clientDataRaw.docs[0].data();

    let generalChecklist = false;
    let lifeChecklist = false;
    let productChecklist = false;
    let videoChecklist = false;
    jobData?.checklists?.forEach((cl, i) => {
      if (cl.name === "General Checklist") {
        jobData?.checklists.splice(i, 1);
        generalChecklist = true;
      }
      if (cl.name === "Life Checklist") {
        jobData?.checklists.splice(i, 1);
        lifeChecklist = true;
      }
      if (cl.name === "Product Checklist") {
        jobData?.checklists.splice(i, 1);
        productChecklist = true;
      }
      if (cl.name === "Video Checklist") {
        jobData?.checklists.splice(i, 1);
        videoChecklist = true;
      }
    });

    if (!generalChecklist) {
      if (clientData?.generalChecklist?.length > 0) {
        let generalChecklist = {
          name: "General Checklist",
          id: uuidv4(),
          checklistItems: clientData.generalChecklist.map((ci) => {
            return {
              id: uuidv4(),
              title: ci,
              checked: false,
            };
          }),
        };

        jobData?.checklists.push(generalChecklist);
      }
    }
    if (!lifeChecklist) {
      if (clientData?.lifeChecklist?.length > 0) {
        let lifeChecklist = {
          name: "Life Checklist",
          id: uuidv4(),
          checklistItems: clientData.lifeChecklist.map((ci) => {
            return {
              id: uuidv4(),
              title: ci,
              checked: false,
            };
          }),
        };
        jobData?.checklists.push(lifeChecklist);
      }
    }
    if (!productChecklist) {
      if (clientData?.productChecklist?.length > 0) {
        let productChecklist = {
          name: "Product Checklist",
          id: uuidv4(),
          checklistItems: clientData.productChecklist.map((ci) => {
            return {
              id: uuidv4(),
              title: ci,
              checked: false,
            };
          }),
        };
        jobData?.checklists.push(productChecklist);
      }
    }
    if (!videoChecklist) {
      if (clientData?.videoChecklist?.length > 0) {
        let videoChecklist = {
          name: "Video Checklist",
          id: uuidv4(),
          checklistItems: clientData.videoChecklist.map((ci) => {
            return {
              id: uuidv4(),
              title: ci,
              checked: false,
            };
          }),
        };
        jobData?.checklists.push(videoChecklist);
      }
    }

    if (jobData && jobData.id) {
      updateDoc(doc(db, "JobData", jobData.id),
        { checklists: jobData?.checklists || [] },
        { merge: true }
      )
        .catch((e) => console.error("Error updating checklists:", e));
    } else {
      console.error("Cannot update checklists: jobData.id is missing");
    }
  } catch (error) {
    console.error("Error in addChecklists function:", error);
  }
}

function addBusinessDays(date, daysToAdd) {
  const year = date.getFullYear();
  let weekday = date.getDay();
  let addDays = weekday >= 3 ? 2 : 0;
  date.setDate(date.getDate() + parseInt(addDays) + parseInt(daysToAdd));
  if (date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() + 2);
  }
  date.setYear(year);
  return date;
}
